/**
 * What the AI changed, derived from the preview contract (VALE-1174).
 *
 * Promoted out of `components/prototypes/ai-review-status/_status.tsx`, where
 * VALE-1050 settled the pattern (variant Y6). The prototype's variants stay
 * standing as the reasoning record; this is the production half, and it is
 * deliberately the *derivation* only — the marks live in `change-marks.tsx`
 * and the interaction in `change-popover.tsx`.
 *
 * Every function here maps FROM the wire contract rather than re-deriving the
 * rule the backend already applied: `action` says whether the row is a create
 * or an update, and `FieldChange.changeType` says whether a field was filled
 * in or overwritten (`enrichment` → new, `conflict` → updated). An absent
 * entry is unchanged. Restating either rule here would put a second definition
 * of "what did the AI do" in front of a reviewer about to commit it.
 */
import type { FieldChange, QuoteSource } from '@/lib/services/ai-analysis';
import { ATTRIBUTE_NOT_RECORDED } from '@/components/attributes';
import { formatAddress } from '@/lib/formatters/contact-formatters';
import { formatBsb } from '@/components/ai-review/compact/format-bsb';
import { formatCurrency } from '@/lib/formatters/australian-formatters';
import { formatSpelledDate } from '@/components/leads/lead-row-format';

/**
 * One reviewed row's change status.
 *
 * A projection of the preview diff row (`ContactDiff`, `InventoryItemDiff`,
 * `PreviewMatterDiff`), carrying only what the review surface renders. The
 * surfaces build it at their adapter boundary — the same place they already
 * turn `diff.proposed` into an entity-shaped record — so a section component
 * never has to know which of the two surfaces it is rendering on.
 */
export interface ReviewItemStatus {
  action: 'create' | 'skip' | 'update';
  /** Present only when `action` is `'update'`. Keys are dot-notation. */
  fieldChanges?: Record<string, FieldChange>;
  /**
   * The row's provenance quote.
   *
   * **Per row, not per field** — that is the contract, not a simplification
   * here. `ProvenanceFields.source` rides the diff *wrapper*, and a create
   * carries no `FieldChange` at all, so there is no per-field quote to read
   * even in principle. The popover therefore labels this as the source for the
   * *item*, and never as evidence for the one field it was opened from.
   */
  source?: QuoteSource;
}

/**
 * Row id → status, keyed by the id the list row and drawer already use
 * (`pendingIdForIndex`). Bound to that encoding rather than a second map key,
 * so a status can never drift from the row it describes.
 */
export type ReviewStatusMap = Record<number, ReviewItemStatus>;

/** What the AI did to a whole row. */
export type ItemChangeKind = 'new' | 'updated' | 'unchanged';

/**
 * What the AI did to one field of a row.
 *
 * `added` is a recorded note being added to (VALE-1951): the row's value is
 * only the text being added, and nothing the note already says is replaced, so
 * it is neither `new` (the field was not empty) nor `updated` (nothing is
 * superseded).
 */
export type FieldChangeKind = 'new' | 'updated' | 'added' | 'unchanged';

export function itemChangeKind(
  status: ReviewItemStatus | undefined
): ItemChangeKind {
  if (!status) return 'unchanged';
  if (status.action === 'create') return 'new';
  if (status.action === 'update' && countFieldChanges(status) > 0)
    return 'updated';
  return 'unchanged';
}

export function fieldChangeFor(
  status: ReviewItemStatus | undefined,
  key: string
): FieldChange | undefined {
  return status?.fieldChanges?.[key];
}

/**
 * Absent, in the one sense the reader sees.
 *
 * `null`, `undefined` and `''` all draw as {@link ATTRIBUTE_NOT_RECORDED}, so a
 * diff between any two of them is a change nobody can perceive.
 */
function isEmptyValue(value: unknown): boolean {
  return value == null || value === '';
}

/**
 * Whether a `FieldChange` states a change the reader can actually see.
 *
 * Structural, because the wire carries whole objects for a contact's `address`
 * and `bankAccount` as well as scalars, and two equal addresses arriving as two
 * object literals are not a change.
 */
function sameValue(a: unknown, b: unknown): boolean {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = Array.from(
    new Set(Object.keys(left).concat(Object.keys(right)))
  );
  for (const key of keys) {
    if (!sameValue(left[key], right[key])) return false;
  }
  return true;
}

/**
 * **A change that changed nothing is not a change** (VALE-1375).
 *
 * The wire decides a field is a `conflict` or an `enrichment` from where the
 * value came from, not from whether it differs from what was already there, so
 * a `FieldChange` whose `existing` equals its `proposed` reaches the surface
 * looking exactly like a real one. Rendered, that is the treatment claiming the
 * AI did something it did not: an owner reviewing an asset met a
 * `Non-estate asset` row tagged `UPDATED` whose popover read `No → No`, with the
 * unchanged old value struck through beside the identical new one.
 *
 * It is answered here, in the one derivation every surface asks, rather than at
 * a mount: the review list, both detail blocks and both drawers all read a
 * field's kind through {@link fieldChangeKind} and an item's through
 * {@link itemChangeKind}, and a guard at any one of them would leave the others
 * still claiming it. This is the same rule VALE-1264 applied to a value the
 * REVIEWER had since changed; this is the half where the wire itself never
 * proposed anything.
 */
export function isNoOpFieldChange(change: FieldChange | undefined): boolean {
  if (!change) return false;
  return sameValue(change.existing, change.proposed);
}

export function fieldChangeKind(
  change: FieldChange | undefined
): FieldChangeKind {
  if (!change) return 'unchanged';
  if (isNoOpFieldChange(change)) return 'unchanged';
  if (change.changeType === 'addition') return 'added';
  return change.changeType === 'enrichment' ? 'new' : 'updated';
}

/**
 * How many of a row's fields the AI actually changed.
 *
 * Counts real changes only, so an `update` row whose every `FieldChange` is a
 * no-op reads as `unchanged` at ITEM level too (VALE-1375) — an item badge
 * saying `UPDATED` over a row with nothing marked inside it is the same false
 * claim one level up.
 */
export function countFieldChanges(
  status: ReviewItemStatus | undefined
): number {
  return Object.values(status?.fieldChanges ?? {}).filter(
    (change) => !isNoOpFieldChange(change)
  ).length;
}

/**
 * A `FieldChange.existing` / `.proposed` value in the form **this block** prints
 * it, so an old bank account reads `232-123 · 321456789` rather than JSON.
 *
 * **Deliberately not `formatFieldChangeValue`**, which is the card path's
 * formatter and the owner of "how does a field-change value read" *there*. Y6
 * puts the old value INLINE beside the new one, and the new one is whatever the
 * detail block itself prints — so the two halves have to be formatted by the
 * same rules or the reader is comparing formatting as much as values. The card
 * formatter renders the same bank account as `BSB: 232123, Account: 321456789`
 * and an address comma-joined, neither of which is what this block prints. What
 * it *does* share is bound: the placeholder, `formatBsb` and `formatAddress` are
 * all the block's own.
 */
export interface ChangeValueContext {
  /**
   * The diff key this value arrived under, dot-notation as the wire sends it.
   *
   * Read only for the parts of an object the surface splits across keys — see
   * the `bsb` branch below.
   */
  key?: string;
  /**
   * The editor kind of the row that will print the NEW value beside this one.
   *
   * The row is the thing that knows a number is currency, so it is asked rather
   * than guessed at. `formatFieldChangeValue`, the card path's formatter, infers
   * it from the number instead (`>= 100 || not an integer` → money), which is a
   * heuristic this one does not need and must not copy: the two would then
   * disagree about `$50.00`.
   */
  editorKind?: string;
}

/** Editor kinds whose value is a currency figure. */
const MONEY_EDITOR_KINDS = ['money', 'valueWithConfirmation', 'marketFigure'];

export function describeChangeValue(
  value: unknown,
  context: ChangeValueContext = {}
): string {
  /* The app's own not-recorded placeholder, bound rather than restated. */
  if (value == null || value === '') return ATTRIBUTE_NOT_RECORDED;

  /*
    **A currency figure, when the ROW says it is one** (VALE-1181).

    Inventory is the first surface whose diff carries bare numbers: a contact's
    values are strings, objects and booleans, so `String(value)` was the whole
    of the scalar case and it was right. An inventory money row prints
    `$4,600.00` and this printed `4400` struck beside it — two formats for one
    comparison, which is precisely what this function exists to prevent.
  */
  if (
    typeof value === 'number' &&
    MONEY_EDITOR_KINDS.includes(context.editorKind ?? '')
  ) {
    return formatCurrency(value);
  }

  /*
    **A date, when the ROW draws one** (VALE-1183).

    The third adoption is the first whose changed values are mostly dates, and
    rule 6's own contract is that the superseded value is printed by the same
    rules as the value beside it — otherwise the reader is comparing formatting
    as much as content. Every `date` row on every surface that marks a change
    reads out through `formatSpelledDate` (`matter-attribute-specs.ts` and
    `contact-attribute-specs.ts` both), and this printed the wire's raw
    `2024-03-01` struck beside the row's own `12 March 2024`.

    Bound to that same helper rather than to a second date format, and it is
    reached the same way the money branch is: the ROW is asked, because an ISO
    string is not distinguishable from any other string by inspection. Contacts
    have a `dateOfBirth` diff key and carried the identical defect since
    VALE-1174 — nothing there is special-cased, it simply starts reading
    correctly.
  */
  if (typeof value === 'string' && context.editorKind === 'date') {
    return formatSpelledDate(`${value}T00:00:00`);
  }

  /*
    **One part of a bank account, keyed on its own** (VALE-1181).

    The object branch below is a CONTACT's bank account, which the diff carries
    whole under one key. Inventory splits the same fact across `itemInfo.bsb`
    and `itemInfo.accountNumber`, so a superseded BSB arrives here as the bare
    string `013000` and printed unformatted beside the row's own
    `062-000 · 12345678`. Same `formatBsb` either way, so the two halves read
    alike whichever shape the surface's diff uses.
  */
  if (
    typeof value === 'string' &&
    (context.key === 'bsb' || context.key?.endsWith('.bsb'))
  ) {
    return formatBsb(value) || ATTRIBUTE_NOT_RECORDED;
  }

  /*
    A junction-table flag reads as a word, not `true` — the contact diff emits
    `isBeneficiary` / `isProvingLpr` as booleans (LF-4031).
  */
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';

  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if ('bsb' in record || 'accountNumber' in record) {
      return (
        [
          formatBsb(record.bsb as string | null | undefined),
          record.accountNumber,
          record.accountName
        ]
          .filter(Boolean)
          .join(' · ') || ATTRIBUTE_NOT_RECORDED
      );
    }

    if ('streetAddress' in record || 'suburb' in record) {
      return (
        formatAddress({
          businessName: (record.businessName as string) ?? undefined,
          streetAddress: (record.streetAddress as string) ?? undefined,
          suburb: (record.suburb as string) ?? undefined,
          state: (record.state as string) ?? undefined,
          postcode: (record.postcode as string) ?? undefined,
          poBox: (record.poBox as string) ?? undefined,
          country: (record.country as string) ?? undefined
        }) || ATTRIBUTE_NOT_RECORDED
      );
    }

    return JSON.stringify(record);
  }

  return String(value);
}

/**
 * Editor kinds whose row draws its value as **something other than text**.
 *
 * `boolean` and `triState` draw a Yes|No segmented pair; `select` draws a
 * status pill (`AttributeReadOut`'s `pill` derivation is scoped to exactly
 * `select` and `boolean`, and the grid draws a live segmented control for
 * `boolean` / `triState`). Ink on the `display` string of any of those lands on
 * text the row never renders. They are still marked — the callers ink the pill
 * or the lit segment through `valueClassName` — they simply are not marked
 * *this* way.
 *
 * **Every other kind renders `display` as plain text**, which is why this is a
 * list of the exceptions rather than a list of the members (VALE-1247). It was
 * an allow-list of four (`text`, `date`, `name`, `address`), and an allow-list
 * over an open set fails silently in one direction: a kind nobody remembered to
 * add reads as un-inkable, loses its ink, and gets a badge that says *changed*
 * beside a value the reader can see perfectly well and cannot tell is new.
 * `bankAccount` was the reported case, and it is the only member of that set
 * currently reachable on a change-marking surface (the contact record's rows
 * are `text`, `date`, `name`, `address`, `select`, `boolean`, `triState` and
 * `bankAccount`) — the other six kinds this widening also covers (`number`,
 * `money`, `multiSelect`, `marketFigure`, `selectWithCustom`,
 * `valueWithConfirmation`, `principalAndMembers`) render text too and would
 * have arrived with the same defect the day a contact or a diffed inventory row
 * grew one.
 */
const CONTROL_DRAWN_EDITOR_KINDS = ['select', 'boolean', 'triState'];

/**
 * Whether a row's value is TEXT the reader actually sees, and so can be inked.
 *
 * **The one definition, used by every surface that marks a change**, the list's
 * detail block and the contact drawer both ask it, because they marked the same
 * field two different ways otherwise (VALE-1208).
 *
 * Y6 marks a change by colouring the value, so the two things that rule it out
 * are the two the badge exists for: a value drawn as a **control**
 * ({@link CONTROL_DRAWN_EDITOR_KINDS}), and a value that is not there at all —
 * an unset row draws the grey `Set …` placeholder, and inking a placeholder
 * would colour the prompt rather than the answer. An empty string is that
 * second case and is excluded explicitly: it is a `string`, so a `typeof` test
 * alone let an emptied `text` row through to be inked invisibly (VALE-1247).
 */
/**
 * **Whether the row draws a value the AI can be credited with** (VALE-1264).
 *
 * A row with no value renders the grey `Set <label>…` placeholder, and a mark
 * that *states* something — a `NEW` badge, the change ink — then credits the AI
 * with a value it did not supply. The contact drawer is a full record editor,
 * so on a **created** contact every empty row read as `new`, which is the
 * reported defect: `Set email…` badged `NEW`.
 *
 * The badge was already invisible on the read-out path by accident rather than
 * by rule — `AttributeReadOut` renders the placeholder INSTEAD of the
 * value-and-badge pair, which VALE-1249 asserted as it behaved and carried as
 * an unhandled member. The accident never covered the whole surface: a live
 * segmented control renders {@link AttributeSpec.badge} unconditionally, so
 * stating it as a rule is what makes it true everywhere.
 *
 * **It governs the badge and the ink, and NOT the popover.** The popover does
 * not credit the AI with a value; it *offers* one — it says what the extraction
 * proposed for this field and lets the reviewer turn it down, which is exactly
 * as useful on a field the record has not got yet. Removing it was tried and
 * rejected: the reject went with it, and a proposed value the reviewer could
 * neither see nor decline is a worse defect than the badge.
 *
 * **A flag row always holds a value, whatever it draws.** A `boolean` has a lit
 * segment either way, and whether a `triState` has been spoken to is exactly
 * what `isUnsetFlag` and `isExtractedFlag` answer. Deferring to them keeps one
 * definition of that question rather than adding a third that would have to
 * agree with both.
 */
export function rowHoldsAValue(field: {
  display?: unknown;
  editor?: { kind?: string };
}): boolean {
  const kind = field.editor?.kind ?? '';
  /* `select` is control-drawn but is not a flag: an unset one draws nothing. */
  if (CONTROL_DRAWN_EDITOR_KINDS.includes(kind) && kind !== 'select')
    return true;
  if (field.display == null) return false;
  /*
    A caller-supplied node draws itself, so it counts. Only a string can be
    empty, and an emptied `text` row IS a string — the case a bare `!= null`
    test lets through, which is the trap `rendersValueAsText` names below.
  */
  if (typeof field.display !== 'string') return true;
  return field.display.trim() !== '';
}

export function rendersValueAsText(field: {
  display?: unknown;
  render?: unknown;
  editor?: { kind?: string };
}): boolean {
  /*
    **A row handing the grid its own node does not draw its value as text, and
    a `display` beside it does not make it do so** (VALE-1181).

    `AttributeGrid` gives a `render` row the whole value slot and skips the
    read-out entirely, so nothing prints `display` — but a picker row supplies
    one anyway, because the treatment reads it to decide whether the row holds a
    value at all. Judging by `display` alone therefore called those rows text
    and routed rule 6 down the inline branch, whose superseded value the grid
    then never drew: the `Bank` row showed `Commonwealth Bank` and not the
    `Westpac` it replaced, while the two rows beside it showed theirs, and the
    drawer showed it correctly for the same field. That is the list-versus-
    drawer drift the treatment exists to prevent, arriving from a new direction.
  */
  if (field.render != null) return false;
  return (
    typeof field.display === 'string' &&
    field.display.trim() !== '' &&
    !CONTROL_DRAWN_EDITOR_KINDS.includes(field.editor?.kind ?? '')
  );
}
